import { DateRangeCalendar } from '@/components/ui/DateRangeCalendar';
import {
  ORDER_STATUS_META,
  ORDER_UI_STATUS_KEYS,
  type OrderUiStatus,
} from '@/constants/calendarStatus';

export type OrderStateFilter = '' | OrderUiStatus;

export interface OrderListFilters {
  startDate: string;
  endDate: string;
  orderState: OrderStateFilter;
}

export const DEFAULT_ORDER_FILTERS: OrderListFilters = {
  startDate: '',
  endDate: '',
  orderState: '',
};

const ORDER_STATE_OPTIONS = ORDER_UI_STATUS_KEYS.map((value) => {
  const meta = ORDER_STATUS_META[value];
  const textClass =
    meta.badgeClass.split(' ').find((cls) => cls.startsWith('text-')) ?? 'text-gray-700';
  return {
    value,
    label: meta.label,
    checkedClass: `border ${meta.badgeClass}`,
    boxClass: meta.dotClass,
    textClass,
  };
});

interface OrderFilterDropdownProps {
  open: boolean;
  draft: OrderListFilters;
  onDraftChange: (next: OrderListFilters | ((prev: OrderListFilters) => OrderListFilters)) => void;
  onApply: () => void;
  onReset: () => void;
}

function FilterCheckItem({
  checked,
  label,
  open,
  onToggle,
  checkedClass = 'border-green-200 bg-green-50',
  boxClass = 'bg-green-600 border-green-600',
  textClass = 'text-green-800',
}: {
  checked: boolean;
  label: string;
  open: boolean;
  onToggle: () => void;
  checkedClass?: string;
  boxClass?: string;
  textClass?: string;
}) {
  return (
    <label
      className={`flex items-center justify-center gap-1 px-1 py-1.5 rounded-md border cursor-pointer transition-colors min-w-0 ${checked
          ? checkedClass
          : 'border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200'
        }`}
    >
      <span
        className={`w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? boxClass : 'bg-white border-gray-300'
          }`}
        aria-hidden
      >
        {checked ? <i className="ri-check-line text-white text-[9px] leading-none" /> : null}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        tabIndex={open ? 0 : -1}
        onChange={onToggle}
      />
      <span
        className={`text-[10px] font-medium truncate min-w-0 leading-tight ${checked ? textClass : 'text-gray-700'
          }`}
      >
        {label}
      </span>
    </label>
  );
}

export default function OrderFilterDropdown({
  open,
  draft,
  onDraftChange,
  onApply,
  onReset,
}: OrderFilterDropdownProps) {
  return (
    <div
      className="absolute top-full left-1/2 z-50 w-screen -ml-[50vw] pointer-events-none"
      aria-hidden={!open}
    >
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`border-b border-gray-200 bg-white shadow-lg transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
            role="region"
            aria-label="Order filters"
          >
            <div className="px-4 py-3 space-y-3 max-h-[min(70vh,32rem)] overflow-y-auto">
              <DateRangeCalendar
                startDate={draft.startDate}
                endDate={draft.endDate}
                onChange={(range) => onDraftChange((prev) => ({ ...prev, ...range }))}
              />

              <div>
                <div className="text-[11px] font-normal text-gray-500 mb-1.5">Order State</div>
                <div className="grid grid-cols-3 gap-1" role="radiogroup" aria-label="Order State">
                  {ORDER_STATE_OPTIONS.map((option) => (
                    <FilterCheckItem
                      key={option.value}
                      checked={draft.orderState === option.value}
                      label={option.label}
                      open={open}
                      checkedClass={option.checkedClass}
                      boxClass={option.boxClass}
                      textClass={option.textClass}
                      onToggle={() =>
                        onDraftChange((prev) => ({
                          ...prev,
                          orderState: prev.orderState === option.value ? '' : option.value,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="px-4 py-4 border-t border-gray-100 flex gap-2 bg-gray-50 p-6">
              <button
                type="button"
                onClick={onReset}
                tabIndex={open ? 0 : -1}
                className="flex-1 h-7 rounded-md border border-gray-200 bg-white text-gray-600 text-[11px] font-semibold cursor-pointer hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={onApply}
                tabIndex={open ? 0 : -1}
                className="flex-1 h-7 rounded-md bg-green-600 text-white text-[11px] font-semibold cursor-pointer hover:bg-green-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
